<script lang="ts" context="module">
  import type { definitions } from "$lib/types/supabase";

  import type { Load } from "@sveltejs/kit";

  export const load: Load = async function ({ page, fetch }) {
    try {
      const { data, error } = await supabase
        .from<AlgoUseWithAlgo>("algo_uses")
        .select(`*, algos ( * )`)
        .eq("slug", page.params.slug)
        .limit(1);
      if (data) {
        const algo_use_with_algo = data[0];
        return { props: { algo_use_with_algo } };
      } else {
        throw new Error("No data");
      }
    } catch (error) {
      console.error(error);
      return {
        status: 302,
        redirect: route_for(Route.docs),
      };
    }
  };
</script>

<script lang="ts">
  import type { JSONSchema7 } from "json-schema";
  import marked from "marked";

  import { Route, route_for } from "$lib/route_list";
  import { supabase } from "$lib/data/supabase";
  import { onMount } from "svelte";
  import type { AlgoUseWithAlgo } from "$lib/types/types";
  export let algo_use_with_algo: AlgoUseWithAlgo;

  let properties;
  let schema;
  let required;
  let property_entries: [key: string, value: any][] = [];

  onMount(() => {
    schema = algo_use_with_algo.algos.params_schema as JSONSchema7;
    properties = schema?.properties;
    required = schema?.required;
    property_entries = Object.entries(properties);
  });
</script>

<svelte:head>
  <title>Docs for {algo_use_with_algo.slug}</title>
</svelte:head>

<div class="mb-5">
  <a href={route_for(Route.docs)}>&lt; All algorithms</a>
</div>

<div class="media mb-5">
  <div class="media-left">
    <figure class="image is-150x150">
      <img
        src={`https://source.boringavatars.com/pixel/150/${algo_use_with_algo.slug}?colors=EC6B01,FE8105,FFA43E,009CA8,0088A2,214775&square`}
        alt="Algorithm identifier"
      />
    </figure>
  </div>
  <div class="media-content">
    <p class="title is-1">{algo_use_with_algo.title}</p>
    <p class="subtitle is-6 is-family-monospace">{algo_use_with_algo.slug}</p>
    <p>{algo_use_with_algo.description}</p>
  </div>
</div>

<div class="mb-6">
  {#if algo_use_with_algo.docs_md}
    <div><a href="#specifics">Specific documentation</a></div>
  {/if}
  {#if algo_use_with_algo.docs_md || algo_use_with_algo.algos.output_md}
    <div><a href="#parameters">General parameters</a></div>
  {/if}
  {#if algo_use_with_algo.algos.output_md}
    <div><a href="#outputs">Outputs</a></div>
  {/if}
</div>

{#if algo_use_with_algo.docs_md}
  <div class="block">
    <div id="specifics" class="is-size-2">Specific documentation</div>

    <div class="block is-italic">
      This section describes any differences between the customised algorithm
      and the general parameters described below.
    </div>
    <div>
      {@html algo_use_with_algo.docs_md
        ? marked(algo_use_with_algo.docs_md)
        : ""}
    </div>
  </div>
{/if}

<div class="block">
  {#if algo_use_with_algo.docs_md}
    <!-- svelte-ignore a11y-invalid-attribute -->
    <div><a href="" class="to-top is-size-6">Top</a></div>
  {/if}
  <div id="parameters" class="is-size-2">General parameters</div>
  {#each property_entries as [key, value]}
    <div id={key} class="mt-3 mb-5">
      <div class="mb-2">
        <a href="#{key}">#</a>
        <strong>{key}</strong>
        {#if value.required || required.includes(key)}
          <em>*Required</em>
        {/if}
        {#if value.type}
          <code>&lbrace;{value.type}&rbrace</code>
        {:else}
          <code>!! TODO: add complex types and references</code>
        {/if}
        {#if value.default}
          (default:
          <em>{value.default}</em>
          )
        {/if}
        {#if value.items?.enum}
          <p>Options:</p>
          {#each value.items.enum as item}
            <span>
              <code>{item}</code>
            </span>
          {/each}
        {/if}
      </div>
      <div>
        {@html value.description ? marked(value.description) : ""}
      </div>
    </div>
  {/each}
</div>

{#if algo_use_with_algo.algos.output_md}
  <div class="block">
    <!-- svelte-ignore a11y-invalid-attribute -->
    <div><a href="" class="to-top is-size-6">Top</a></div>
    <div id="outputs" class="is-size-2">Outputs</div>

    <div class="rendered-outputs">
      {@html algo_use_with_algo.algos.output_md
        ? marked(algo_use_with_algo.algos.output_md)
        : ""}
    </div>
  </div>
{/if}

<style lang="scss">
  .rendered-outputs {
    > :global(p) {
      margin-bottom: 1em;
    }
    > :global(ul) {
      margin-bottom: 1em;
    }
    > :global(li) {
      margin-bottom: 1em;
      list-style-type: disc;
    }
  }
</style>
